// Reading session endpoints: create / get / list, review, and the SSE
// start stream. The interactive draw + follow-up SSE flows still live
// in Reading.tsx because they're tightly coupled to UI state machines.

import { ApiError, Auth, call, handleUnauthorized } from './core';
import type {
  InteractiveSession,
  SessionListPage,
  SessionState,
} from './types';

export const readingApi = {
  // STA-151 — submit a 1-5 star review on a completed session.
  submitMentorReview: (
    sessionId: string,
    payload: { rating: number; review_text?: string | null },
  ) =>
    call<{ id: string; teacher_id: string; session_id: string; rating: number; review_text: string | null }>(
      `/readings/sessions/${sessionId}/review`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  // Reading sessions
  createSession: (payload: {
    category_id?: string;
    mentor_id: string;
    intention: string;
    spread_size: number;
  }) =>
    call<SessionState>('/readings/sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // STA-116 — interactive variant: server allocates a pool but defers drawing.
  // Frontend renders pool_size face-down placeholders, user picks spread_size,
  // then commits via interpretPicked() which streams SSE.
  //
  // STA-152 — prefer ``spread_id`` over ``spread_size``: when provided the
  // server reads ``position_count`` from the spreads table and ignores the
  // explicit size, so labels + cost stay in sync with the admin-curated row.
  createInteractiveSession: (payload: {
    category_id?: string;
    mentor_id: string;
    intention: string;
    spread_size: number;
    spread_id?: string;
  }) =>
    call<InteractiveSession>('/readings/sessions', {
      method: 'POST',
      body: JSON.stringify({ ...payload, interactive: true }),
    }),

  getSession: (id: string) => call<SessionState>(`/readings/sessions/${id}`),

  listSessions: (
    params: {
      status?: string;
      category?: string;
      cursor?: string | null;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status_', params.status);
    if (params.category) q.set('category', params.category);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return call<SessionListPage>(`/readings/sessions${qs ? `?${qs}` : ''}`);
  },

  // SSE — streaming reading. Returns an async iterator yielding parsed
  // event objects from the session's /start endpoint.
  async *startStream(
    sessionId: string,
  ): AsyncGenerator<Record<string, unknown>, void, unknown> {
    const t = Auth.getToken();
    const res = await fetch(`/api/v1/readings/sessions/${sessionId}/start`, {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: JSON.stringify({}),
    });
    handleUnauthorized(res.status, /* authed */ true);
    if (!res.ok || !res.body) {
      throw new ApiError(res.status, await res.text(), 'SSE failed');
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE events separated by blank line; each event is "data: <json>\n"
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const dataLine = block
          .split('\n')
          .find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const json = dataLine.slice(5).trim();
        if (!json) continue;
        try {
          yield JSON.parse(json);
        } catch {
          // ignore malformed
        }
      }
    }
  },
};
